import "@/index.css";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import TaskSubmissionPage from "@/pages/TaskSubmissionPage";
import SubmitTaskChooserPage from "@/pages/SubmitTaskChooserPage";
import TaskLogPage from "@/pages/TaskLogPage";
import BrandWorkspacePage from "@/pages/BrandWorkspacePage";
import AssetLibraryPage from "@/pages/AssetLibraryPage";
import SettingsPage from "@/pages/SettingsPage";
import ListingQualityPage from "@/pages/ListingQualityPage";
import SKULiveStatusPage from "@/pages/SKULiveStatusPage";
import CreativeAnalyzerPage from "@/pages/CreativeAnalyzerPage";
import CreativeLogPage from "@/pages/CreativeLogPage";
import EANRequestPage from "@/pages/EANRequestPage";
import NewProductLaunchPage from "@/pages/NewProductLaunchPage";
import AssigneeTrackerPage from "@/pages/AssigneeTrackerPage";
import SKUDNAPage from "@/pages/SKUDNAPage";
import CompetitorRadarPage from "@/pages/CompetitorRadarPage";
import CompetitorRepositoryPage from "@/pages/CompetitorRepositoryPage";
import DashboardLayout from "@/components/layout/DashboardLayout";

// Auth callback handler - processes session_id from URL fragment
const AuthCallback = () => {
  const navigate = useNavigate();
  const { processSession, setUserData } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const handleCallback = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        try {
          const userData = await processSession(sessionId);
          setUserData(userData);
          // Clear hash and redirect to dashboard
          window.history.replaceState(null, '', '/dashboard');
          navigate('/dashboard', { replace: true, state: { user: userData } });
          toast.success(`Welcome, ${userData.name}!`);
        } catch (error) {
          console.error('Auth callback error:', error);
          toast.error(error.message || 'Authentication failed');
          navigate('/login', { replace: true });
        }
      } else {
        navigate('/login', { replace: true });
      }
    };

    handleCallback();
  }, [navigate, processSession, setUserData]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2EB4AC] mx-auto mb-4"></div>
        <p className="text-slate-600">Signing you in...</p>
      </div>
    </div>
  );
};

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  
  // If user data was passed from AuthCallback, skip loading
  if (location.state?.user) {
    return children;
  }
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2EB4AC]"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2EB4AC]"></div>
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// App Router with session_id detection DURING RENDER (not in useEffect)
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id synchronously during render
  // This prevents race conditions by processing new session_id FIRST
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/login" element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      } />
      <Route path="/" element={
        <ProtectedRoute>
          <DashboardLayout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="submit" element={<SubmitTaskChooserPage />} />
        <Route path="submit/design" element={<TaskSubmissionPage />} />
        <Route path="submit/content" element={<TaskSubmissionPage />} />
        <Route path="submit/audio" element={<TaskSubmissionPage />} />
        <Route path="tasks" element={<TaskLogPage />} />
        <Route path="tasks/brand/:brandName" element={<BrandWorkspacePage />} />
        <Route path="asset-library" element={<AssetLibraryPage />} />
        <Route path="listing-quality" element={<ListingQualityPage />} />
        <Route path="sku-live-status" element={<SKULiveStatusPage />} />
        <Route path="creative-analyzer" element={<CreativeAnalyzerPage />} />
        <Route path="creative-log" element={<CreativeLogPage />} />
        <Route path="ean-request" element={<EANRequestPage />} />
        <Route path="new-product-launch" element={<NewProductLaunchPage />} />
        <Route path="assignee/:assigneeId" element={<AssigneeTrackerPage />} />
        <Route path="sku-dna" element={<SKUDNAPage />} />
        <Route path="competitor-radar" element={<CompetitorRadarPage />} />
        <Route path="competitor-repository" element={<CompetitorRepositoryPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
